import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import client, { apiErr, API, mediaUrl, downloadMedia } from "@/lib/apiClient";
import { TOOLS, buildPrompt } from "@/config/tools";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, UploadCloud, Download, Copy, Wand2, Sparkles, RefreshCw, Trash2, Check, Zap, X } from "lucide-react";

const PROGRESS_IMAGE = ["Analyzing your request...", "Generating image...", "Finishing touches..."];
const PROGRESS_VIDEO = ["Analyzing your request...", "Generating video (this may take a minute)...", "Finishing touches..."];

export default function Studio() {
  const { service } = useParams();
  const navigate = useNavigate();
  const { refreshWallet, wallet } = useAuth();
  const cfg = TOOLS[service];
  const fileRef = useRef();

  const [values, setValues] = useState({});
  const [model, setModel] = useState("");
  const [aspect, setAspect] = useState(cfg?.aspectRatios?.[0] || "1:1");
  const [quantity, setQuantity] = useState(cfg?.quantities?.[0] || 1);
  const [duration, setDuration] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [models, setModels] = useState([]);
  const [pricing, setPricing] = useState({});
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | processing | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const isVideo = cfg?.media === "video";

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!cfg) return;
    setValues({}); setUpload(null); setResult(null); setStatus("idle");
    setAspect(cfg.aspectRatios[0]); setQuantity(cfg.quantities[0]);
    setDuration(null); setResolution(null);
    setModel("");
  }, [service]); // eslint-disable-line

  useEffect(() => {
    // Filter models by the service so video tools show video models and vice versa.
    client.get(`/models?service=${encodeURIComponent(service)}`).then(({ data }) => {
      setModels(data);
      const rec = data.find((m) => m.recommended) || data[0];
      if (rec) setModel((cur) => cur || rec.customer_model);
    }).catch(() => {});
    client.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, [service]);

  const poll = useCallback(async (gid) => {
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await client.get(`/generations/${gid}`);
        const isVid = data.service && data.service.startsWith("text_to_video") || data.service === "image_to_video" || data.service === "video_extend";
        const steps = isVid ? PROGRESS_VIDEO : PROGRESS_IMAGE;
        if (data.status === "processing") setProgress((p) => Math.min(p + 1, steps.length - 1));
        if (data.status === "completed") {
          clearInterval(timer);
          setResult(data); setStatus("done");
          refreshWallet();
          toast.success("Your creative is ready!");
        } else if (data.status === "failed") {
          clearInterval(timer);
          setStatus("error"); setResult(data);
          refreshWallet();
          toast.error("Generation failed — no credits were charged.");
        }
      } catch (e) { /* keep polling */ }
      // Video jobs can take up to ~2 minutes; allow ~4 minutes of polling (200 * 1.2s = 240s).
      if (tries > 200) { clearInterval(timer); setStatus("error"); }
    }, 1200);
  }, [refreshWallet]);

  if (!cfg) return <div className="p-16 text-center text-muted-foreground">Unknown tool. <Button variant="link" onClick={() => navigate("/create")}>Back</Button></div>;

  const selModel = models.find((m) => m.customer_model === model);
  const tier = selModel?.tier || (model === "premium" ? "premium" : "basic");
  const perUnit = pricing?.[service]?.[tier] ?? 0;
  const cost = perUnit * quantity;
  const availableDurations = (isVideo && selModel?.default_durations) || [];
  const availableResolutions = (isVideo && selModel?.default_resolutions) || [];
  // Effective picks: prefer the user's selection if it's still valid for the current model,
  // otherwise fall back to the first admin-enabled option. Computed at render time to avoid
  // the model-swap race between /models fetch and setState effects.
  const effectiveDuration = availableDurations.includes(duration) ? duration : (availableDurations[0] || null);
  const effectiveResolution = availableResolutions.includes(resolution) ? resolution : (availableResolutions[0] || null);
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload({ ...data, preview: URL.createObjectURL(file) });
    } catch (e) {
      toast.error(apiErr(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    // validation
    if (cfg.upload?.required && !upload) return toast.error("Please upload an image first.");
    if (cfg.prompt?.required && !values.prompt) return toast.error("Please describe what you want to create.");
    const missing = cfg.fields.find((f) => f.required && !values[f.key]);
    if (missing) return toast.error(`Please fill in "${missing.label}".`);

    setStatus("processing"); setProgress(0); setResult(null);
    try {
      const body = {
        service, model, prompt: buildPrompt(cfg, values),
        aspect_ratio: aspect, quantity,
        duration: isVideo ? effectiveDuration : null,
        resolution: isVideo ? effectiveResolution : null,
        input_upload_ids: upload ? [upload.id] : [],
        options: { ...values, aspect_ratio: aspect, ...(isVideo ? { duration: effectiveDuration, resolution: effectiveResolution } : {}) },
      };
      const { data } = await client.post("/generations", body);
      poll(data.id);
    } catch (e) {
      setStatus("idle");
      toast.error(apiErr(e, "Could not start generation"));
    }
  };

  const Icon = Icons[cfg.icon] || Sparkles;
  const download = async (url, idx = 0) => {
    try {
      await downloadMedia(url, `adgen-${service}-${Date.now()}-${idx + 1}`);
    } catch (e) {
      toast.error(apiErr(e, "Download failed"));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
      <button onClick={() => navigate("/create")} className="mb-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <Icons.ChevronLeft className="h-4 w-4" /> All tools
      </button>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,440px)_1fr]">
        {/* CONFIG */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
            <div>
              <h1 className="text-xl font-bold">{cfg.title}</h1>
              <p className="text-sm text-muted-foreground">{cfg.subtitle}</p>
            </div>
          </div>

          {cfg.upload && (
            <div>
              <Label>{cfg.upload.label}{cfg.upload.required && " *"}</Label>
              <input ref={fileRef} type="file" accept="image/*" hidden data-testid="studio-file-input" onChange={(e) => doUpload(e.target.files[0])} />
              {upload ? (
                <div className="mt-1.5 relative overflow-hidden rounded-xl border border-border">
                  <img src={upload.preview} alt="upload" className="max-h-56 w-full object-contain bg-secondary/40" />
                  <button onClick={() => setUpload(null)} className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-background/90 shadow"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <button data-testid="studio-upload-button" onClick={() => fileRef.current?.click()} className="mt-1.5 flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border py-8 text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
                  {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <UploadCloud className="h-6 w-6" />}
                  <span className="text-sm">{uploading ? "Uploading…" : "Click to upload an image"}</span>
                </button>
              )}
            </div>
          )}

          {cfg.prompt && (
            <div>
              <Label>{cfg.prompt.label}{cfg.prompt.required && " *"}</Label>
              <Textarea data-testid="studio-prompt-input" rows={cfg.prompt.rows} placeholder={cfg.prompt.placeholder} value={values.prompt || ""} onChange={(e) => set("prompt", e.target.value)} className="mt-1.5 resize-none" />
            </div>
          )}

          {cfg.fields.map((f) => (
            <div key={f.key}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "select" ? (
                <Select value={values[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                  <SelectTrigger data-testid={`studio-field-${f.key}`} className="mt-1.5"><SelectValue placeholder={`Choose ${f.label.toLowerCase()}`} /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid={`studio-field-${f.key}`} placeholder={f.placeholder} value={values[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1.5" />
              )}
            </div>
          ))}

          {/* Model selector */}
          <div>
            <Label className="mb-1.5 block">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="model-select" className="w-full">
                <SelectValue placeholder="Choose a model" />
              </SelectTrigger>
              <SelectContent>
                {models.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    No models available.<br />Enable a provider in Admin.
                  </div>
                ) : models.map((m) => {
                  const credits = pricing?.[service]?.[m.tier];
                  return (
                    <SelectItem key={m.customer_model} value={m.customer_model} data-testid={`model-option-${m.customer_model}`}>
                      <div className="flex w-full items-center justify-between gap-3">
                        <span className="font-medium">{m.label}</span>
                        {credits != null && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                            <Zap className="h-3 w-3" fill="currentColor" />{credits} credits
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selModel?.tagline && <p className="mt-1.5 text-xs text-muted-foreground">{selModel.tagline}</p>}
          </div>

          {cfg.aspectRatios.length > 1 && (
            <div>
              <Label className="mb-1.5 block">Aspect ratio</Label>
              <div className="flex flex-wrap gap-2">
                {cfg.aspectRatios.map((r) => (
                  <button key={r} type="button" data-testid={`aspect-${r}`} onClick={() => setAspect(r)}
                    className={`rounded-lg border px-3.5 py-1.5 text-sm font-medium transition-colors ${aspect === r ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}>{r}</button>
                ))}
              </div>
            </div>
          )}

          {isVideo && availableDurations.length > 0 && (
            <div>
              <Label className="mb-1.5 block">Duration</Label>
              <div className="flex flex-wrap gap-2">
                {availableDurations.map((d) => (
                  <button key={d} type="button" data-testid={`duration-${d}`} onClick={() => setDuration(d)}
                    className={`h-10 rounded-lg border px-4 text-sm font-semibold transition-colors ${effectiveDuration === d ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}>{d}s</button>
                ))}
              </div>
            </div>
          )}

          {isVideo && availableResolutions.length > 0 && (
            <div>
              <Label className="mb-1.5 block">Resolution</Label>
              <div className="flex flex-wrap gap-2">
                {availableResolutions.map((r) => (
                  <button key={r} type="button" data-testid={`resolution-${r}`} onClick={() => setResolution(r)}
                    className={`h-10 rounded-lg border px-4 text-sm font-semibold transition-colors ${effectiveResolution === r ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}>{r}</button>
                ))}
              </div>
            </div>
          )}

          {cfg.quantities.length > 1 && (
            <div>
              <Label className="mb-1.5 block">{isVideo ? "Number of videos" : "Number of images"}</Label>
              <div className="flex gap-2">
                {cfg.quantities.map((q) => (
                  <button key={q} type="button" data-testid={`quantity-${q}`} onClick={() => setQuantity(q)}
                    className={`h-10 w-12 rounded-lg border text-sm font-semibold transition-colors ${quantity === q ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40"}`}>{q}</button>
                ))}
              </div>
            </div>
          )}

          <Button data-testid="generate-submit-button" size="lg" className="w-full rounded-full text-base" disabled={status === "processing"} onClick={generate}>
            {status === "processing" ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating…</> : <><Sparkles className="mr-2 h-5 w-5" /> Generate — {cost} Credits</>}
          </Button>
          <p className="text-center text-xs text-muted-foreground">You have {wallet.available_balance} credits</p>
        </div>

        {/* PREVIEW */}
        <div className="min-h-[420px] rounded-2xl border border-border bg-secondary/30 p-6">
          {status === "idle" && (
            <div className="grid h-full min-h-[380px] place-items-center text-center">
              <div>
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-background text-muted-foreground shadow-sm"><Icon className="h-8 w-8" /></span>
                <p className="mt-4 font-semibold">Your preview will appear here</p>
                <p className="text-sm text-muted-foreground">Configure and hit Generate.</p>
              </div>
            </div>
          )}

          {status === "processing" && (
            <div className="grid h-full min-h-[380px] place-items-center">
              <div className="w-full max-w-sm text-center">
                <div className="shimmer mx-auto mb-6 h-48 w-full rounded-xl" />
                <p className="font-display text-lg font-bold">{isVideo ? "Generating your video…" : "Creating your creative…"}</p>
                <div className="mt-4 space-y-2">
                  {(isVideo ? PROGRESS_VIDEO : PROGRESS_IMAGE).map((s, i) => (
                    <div key={i} className={`flex items-center justify-center gap-2 text-sm ${i <= progress ? "text-foreground" : "text-muted-foreground/50"}`}>
                      {i < progress ? <Check className="h-4 w-4 text-primary" /> : i === progress ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <span className="h-4 w-4" />}
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="grid h-full min-h-[380px] place-items-center text-center">
              <div className="w-full max-w-md">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-destructive/10 text-destructive"><X className="h-7 w-7" /></span>
                <p className="mt-4 font-semibold">{isVideo ? "We couldn't generate your video." : "We couldn't generate your image."}</p>
                <p className="text-sm text-muted-foreground">No credits were charged.</p>
                {result?.error_message && (
                  <details data-testid="error-details" className="mt-4 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-left">
                    <summary className="cursor-pointer text-xs font-semibold text-destructive">Show details</summary>
                    <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                      {result.error_code && (
                        <p><span className="font-semibold text-foreground">Code:</span> {result.error_code}</p>
                      )}
                      {result.provider_used && (
                        <p><span className="font-semibold text-foreground">Provider:</span> {result.provider_used}{result.provider_model_used ? ` · ${result.provider_model_used}` : ""}</p>
                      )}
                      <p className="whitespace-pre-wrap break-words"><span className="font-semibold text-foreground">Error:</span> {result.error_message}</p>
                    </div>
                  </details>
                )}
                <Button className="mt-5 rounded-full" data-testid="try-again-button" onClick={generate}>Try again</Button>
              </div>
            </div>
          )}

          {status === "done" && result && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 rounded-full bg-background px-3 py-1 text-xs font-semibold shadow-sm">
                  <span className="capitalize">{result.model}</span> · <Zap className="h-3 w-3 text-primary" fill="currentColor" /> {result.credits_charged} credits
                </span>
              </div>
              <div className={`grid gap-4 ${result.output_urls.length > 1 ? "sm:grid-cols-2" : ""}`}>
                {result.output_urls.map((u, i) => (
                  <div key={i} data-testid={`result-image-${i}`} className="animate-fade-up overflow-hidden rounded-xl border border-border bg-background">
                    {isVideo || u.endsWith(".mp4") || u.endsWith(".webm") ? (
                      <video src={mediaUrl(u)} controls className="w-full" />
                    ) : (
                      <img src={mediaUrl(u)} alt="result" className="w-full object-cover" />
                    )}                    <div className="flex flex-wrap gap-2 p-3">
                      <Button size="sm" data-testid="result-download-hd-button" onClick={() => download(u, i)} className="rounded-full"><Download className="mr-1 h-4 w-4" /> Download</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => client.post(`/generations/${result.id}/variation`).then(() => { toast.success("Variation started — check My Creations"); }).catch((e) => toast.error(apiErr(e)))}><Copy className="mr-1 h-4 w-4" /> Variation</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate("/create/image_edit")}><Wand2 className="mr-1 h-4 w-4" /> Edit</Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" className="rounded-full" onClick={generate}><RefreshCw className="mr-1 h-4 w-4" /> Generate again</Button>
                <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={() => navigate("/library")}>View in library</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
